// Angular Imports
import { Injectable, computed, inject } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Router } from '@angular/router'
import { environment } from '@environment'
// Angular Material Imports
import { MatDialog } from '@angular/material/dialog'
// Thirdparty Imports
import { Observable, catchError, map, throwError } from 'rxjs'
import { ECompany } from '@shared/models'
import { AuthStore } from '@shared/store'
// Thirdparty Imports

@Injectable({
	providedIn: 'root',
})
export class AuthService {
	readonly #http = inject(HttpClient)
	readonly #router = inject(Router)
	readonly #authStore = inject(AuthStore)
	readonly #dialog = inject(MatDialog)

	companies = computed(() => this.#authStore.companies())

	/**
	 * Updates the access token using the provided refresh token
	 *
	 * @param {string} refreshToken - The refresh token to obtain new access and refresh tokens
	 * @returns {Observable<{accessToken: string, refreshToken: string}>} An Observable that emits an object containing the new access token, refresh token, and companies
	 * @throws {Error} Throws an error if the response does not contain the expected tokens or if an unexpected error occurs during the token refresh process
	 */
	updateAccessToken(refreshToken: string | null): Observable<string> {
		// console.log('updateAccessToken')

		const headers = new HttpHeaders({
			// Authorization: '',
		})

		let payload: object = {}
		let url: string = ''

		if (this.companies().includes(ECompany.zee)) {
			payload = {
				refreshToken: refreshToken,
			}

			url = `${environment.gateway}/auth/refresh-token`
			return this.#http.post(url, payload, { headers }).pipe(
				catchError((error) => {
					this.#authStore.signout()
					this.#router.navigate(['/signout'])
					this.#dialog.closeAll()
					return throwError(() => error)
				}),
				map((response: any) => {
					if (response?.token && response?.refreshToken) {
						const newAccessToken: string = response.token
						const newRefreshToken: string = response.refreshToken

						this.#authStore.updateAccessAndRefreshToken({
							accessToken: newAccessToken,
							refreshToken: newRefreshToken,
						})

						return newAccessToken
					} else {
						throw new Error(
							'An unexpected error occurred when we tried to refresh the authentication tokens'
						)
					}
				})
			)
		} else {
			headers.set('Authorization', '')
			payload = {
				refresh_token: refreshToken,
			}
			url = `${environment.gateway}/oms/auth/refresh`
			return this.#http.post(url, payload, { headers }).pipe(
				catchError((error) => {
					this.#authStore.signout()
					this.#router.navigate(['/signout'])
					this.#dialog.closeAll()
					return throwError(() => error)
				}),
				map((response: any) => {
					if (response?.data?.access_token && response?.data?.refresh_token) {
						const newAccessToken: string = response.data.access_token
						const newRefreshToken: string = response.data.refresh_token

						this.#authStore.updateAccessAndRefreshToken({
							accessToken: newAccessToken,
							refreshToken: newRefreshToken,
						})

						return newAccessToken
					} else {
						throw new Error(
							'An unexpected error occurred when we tried to refresh the authentication tokens'
						)
					}
				})
			)
		}
	}

	/**
	 * Redirects the user to the Active Directory sign-out page
	 * The function constructs the sign-out URL using environment variables for the gateway and the redirect URI
	 * Once constructed, the window location is replaced with the sign-out URL, which initiates the sign-out process
	 * @returns {void}
	 */
	redirectToSignoutWithActiveDirectory(): void {
		window.location.replace(
			`${environment.gateway}/logout?redirect_uri=${environment.signoutRedirectUrl}`
		)
	}
}
